import { AUDIO_ERROR_CODES } from '@shared/constants/error-codes';
import { AUDIO_TRANSCRIPTION_LIMITS } from '@shared/constants/audio-transcription';
import type {
  AudioAttachmentMime,
  FinalizeAudioUploadResponseDTO,
  PrepareAudioUploadResponseDTO,
} from '@shared/schemas/audio-transcription';
import { ApiError } from '@/lib/api-error';
import {
  deleteChatAttachment,
  finalizeAudioUpload,
  prepareAudioUpload,
  requestAudioUploadParts,
} from './audio-transcription.api';

const PART_RETRY_ATTEMPTS = 3;
const PART_REQUEST_BATCH_SIZE = 32;

export class AudioUploadAbortError extends Error {
  constructor() {
    super('AUDIO_UPLOAD_ABORTED');
    this.name = 'AudioUploadAbortError';
  }
}

export function isAudioUploadAbortError(error: unknown): boolean {
  return error instanceof AudioUploadAbortError
    || (error instanceof DOMException && error.name === 'AbortError')
    || (error instanceof Error && (error.name === 'AbortError' || error.message === 'AUDIO_UPLOAD_ABORTED'));
}

export async function runWithConcurrency<T>(
  items: readonly T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  if (items.length === 0) {
    return;
  }

  let nextIndex = 0;
  const workerCount = Math.min(Math.max(concurrency, 1), items.length);

  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      const item = items[index];
      if (item === undefined) {
        return;
      }
      await worker(item, index);
    }
  }));
}

function assertNotAborted(signal: AbortSignal) {
  if (signal.aborted) {
    throw new AudioUploadAbortError();
  }
}

function delay(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new AudioUploadAbortError());
      return;
    }

    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(new AudioUploadAbortError());
    };

    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function readEtag(xhr: XMLHttpRequest) {
  return xhr.getResponseHeader('etag') ?? xhr.getResponseHeader('ETag') ?? '';
}

function putBlobWithXhr(input: {
  blob: Blob;
  headers: Record<string, string>;
  onProgress?: (loadedBytes: number) => void;
  signal: AbortSignal;
  url: string;
}): Promise<string> {
  return new Promise((resolve, reject) => {
    if (input.signal.aborted) {
      reject(new AudioUploadAbortError());
      return;
    }

    const xhr = new XMLHttpRequest();
    let settled = false;

    const settle = (callback: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      input.signal.removeEventListener('abort', onAbort);
      callback();
    };

    const onAbort = () => {
      xhr.abort();
      settle(() => reject(new AudioUploadAbortError()));
    };

    input.signal.addEventListener('abort', onAbort, { once: true });

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        input.onProgress?.(event.loaded);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = readEtag(xhr).trim();
        if (!etag) {
          settle(() => reject(new ApiError({
            code: AUDIO_ERROR_CODES.AUDIO_UPLOAD_INCOMPLETE,
            message: 'ETag manquant après l’envoi.',
            status: xhr.status,
          })));
          return;
        }
        settle(() => resolve(etag));
        return;
      }

      settle(() => reject(new ApiError({
        code: AUDIO_ERROR_CODES.AUDIO_UPLOAD_INCOMPLETE,
        message: `L’envoi a échoué (${xhr.status}).`,
        status: xhr.status,
      })));
    };

    xhr.onerror = () => {
      settle(() => reject(new ApiError({
        code: AUDIO_ERROR_CODES.AUDIO_UPLOAD_INCOMPLETE,
        message: 'L’envoi du fichier audio a échoué.',
        status: 0,
      })));
    };

    xhr.onabort = () => {
      settle(() => reject(new AudioUploadAbortError()));
    };

    xhr.open('PUT', input.url);
    for (const [header, value] of Object.entries(input.headers)) {
      xhr.setRequestHeader(header, value);
    }
    xhr.send(input.blob);
  });
}

export async function putBlobWithRetry(input: {
  blob: Blob;
  headers: Record<string, string>;
  onProgress?: (loadedBytes: number) => void;
  refresh?: () => Promise<{ headers: Record<string, string>; url: string }>;
  signal: AbortSignal;
  url: string;
}): Promise<string> {
  let url = input.url;
  let headers = input.headers;
  let lastError: unknown;

  for (let attempt = 0; attempt < PART_RETRY_ATTEMPTS; attempt += 1) {
    assertNotAborted(input.signal);
    try {
      return await putBlobWithXhr({
        blob: input.blob,
        headers,
        onProgress: input.onProgress,
        signal: input.signal,
        url,
      });
    } catch (error) {
      lastError = error;
      if (isAudioUploadAbortError(error) || attempt === PART_RETRY_ATTEMPTS - 1) {
        throw error;
      }
      if (error instanceof ApiError && error.status >= 200 && error.status < 300) {
        throw error;
      }
      await delay(400 * (2 ** attempt), input.signal);
      if (input.refresh) {
        const next = await input.refresh();
        url = next.url;
        headers = next.headers;
      }
    }
  }

  throw lastError;
}

function slicePart(file: File, partNumber: number, partSizeBytes: number, partCount: number) {
  const start = (partNumber - 1) * partSizeBytes;
  const end = partNumber === partCount ? file.size : Math.min(start + partSizeBytes, file.size);
  return file.slice(start, end);
}

async function uploadSimplePut(input: {
  file: File;
  onProgress?: (percent: number) => void;
  prepared: PrepareAudioUploadResponseDTO;
  signal: AbortSignal;
}): Promise<{ etag: string }> {
  const simplePut = input.prepared.simplePut;
  if (!simplePut) {
    throw new ApiError({
      code: AUDIO_ERROR_CODES.AUDIO_UPLOAD_INCOMPLETE,
      message: 'URL d’envoi simple manquante.',
      status: 500,
    });
  }

  const etag = await putBlobWithRetry({
    blob: input.file,
    headers: simplePut.headers,
    onProgress: (loadedBytes) => {
      input.onProgress?.(Math.min(100, Math.round((loadedBytes / Math.max(input.file.size, 1)) * 100)));
    },
    signal: input.signal,
    url: simplePut.url,
  });

  input.onProgress?.(100);
  return { etag };
}

async function uploadMultipart(input: {
  file: File;
  onProgress?: (percent: number) => void;
  prepared: PrepareAudioUploadResponseDTO;
  signal: AbortSignal;
}): Promise<{ parts: Array<{ etag: string; partNumber: number }> }> {
  const multipart = input.prepared.multipart;
  if (!multipart) {
    throw new ApiError({
      code: AUDIO_ERROR_CODES.AUDIO_UPLOAD_INCOMPLETE,
      message: 'Session d’envoi multipart manquante.',
      status: 500,
    });
  }

  const partProgress = new Map<number, number>();
  const reportProgress = () => {
    let loaded = 0;
    for (const value of partProgress.values()) {
      loaded += value;
    }
    input.onProgress?.(Math.min(100, Math.round((loaded / Math.max(input.file.size, 1)) * 100)));
  };

  const completedParts: Array<{ etag: string; partNumber: number }> = [];

  for (let start = 1; start <= multipart.partCount; start += PART_REQUEST_BATCH_SIZE) {
    assertNotAborted(input.signal);
    const partNumbers = Array.from(
      { length: Math.min(PART_REQUEST_BATCH_SIZE, multipart.partCount - start + 1) },
      (_, index) => start + index,
    );
    const signed = await requestAudioUploadParts(input.prepared.attachmentId, { partNumbers });

    await runWithConcurrency(
      signed.parts,
      AUDIO_TRANSCRIPTION_LIMITS.maxConcurrentParts,
      async (part) => {
        const blob = slicePart(input.file, part.partNumber, multipart.partSizeBytes, multipart.partCount);
        const etag = await putBlobWithRetry({
          blob,
          headers: part.headers,
          onProgress: (loadedBytes) => {
            partProgress.set(part.partNumber, loadedBytes);
            reportProgress();
          },
          refresh: async () => {
            const refreshed = await requestAudioUploadParts(input.prepared.attachmentId, {
              partNumbers: [part.partNumber],
            });
            const nextPart = refreshed.parts[0];
            if (!nextPart) {
              throw new ApiError({
                code: AUDIO_ERROR_CODES.AUDIO_UPLOAD_INCOMPLETE,
                message: 'Impossible de renouveler l’URL de partie.',
                status: 500,
              });
            }
            return { headers: nextPart.headers, url: nextPart.url };
          },
          signal: input.signal,
          url: part.url,
        });
        partProgress.set(part.partNumber, blob.size);
        reportProgress();
        completedParts.push({ etag, partNumber: part.partNumber });
      },
    );
  }

  input.onProgress?.(100);
  return {
    parts: completedParts.slice().sort((left, right) => left.partNumber - right.partNumber),
  };
}

export async function uploadAudioFileDirect(input: {
  conversationId?: string;
  file: File;
  mediaType: AudioAttachmentMime;
  onPrepared?: (attachmentId: string) => void;
  onProgress?: (percent: number) => void;
  onStatusChange?: (status: 'uploading' | 'validating') => void;
  signal: AbortSignal;
}): Promise<FinalizeAudioUploadResponseDTO> {
  assertNotAborted(input.signal);

  const prepared = await prepareAudioUpload({
    conversationId: input.conversationId,
    filename: input.file.name,
    mediaType: input.mediaType,
    sizeBytes: input.file.size,
  });

  input.onPrepared?.(prepared.attachmentId);
  input.onStatusChange?.('uploading');
  input.onProgress?.(0);

  try {
    if (prepared.strategy === 'simple_put') {
      const { etag } = await uploadSimplePut({
        file: input.file,
        onProgress: input.onProgress,
        prepared,
        signal: input.signal,
      });
      assertNotAborted(input.signal);
      input.onStatusChange?.('validating');
      return await finalizeAudioUpload(prepared.attachmentId, { etag });
    }

    const { parts } = await uploadMultipart({
      file: input.file,
      onProgress: input.onProgress,
      prepared,
      signal: input.signal,
    });
    assertNotAborted(input.signal);
    input.onStatusChange?.('validating');
    return await finalizeAudioUpload(prepared.attachmentId, { parts });
  } catch (error) {
    if (isAudioUploadAbortError(error)) {
      await deleteChatAttachment(prepared.attachmentId).catch(() => undefined);
    }
    throw error;
  }
}
